'use client'

import { Button, cn, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from '@mentora/ui-components'

import { AlertCircle, CheckCircle2, Paperclip, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRef, useState } from 'react'

const ACCEPT_IMAGES = 'image/jpeg,image/png,image/webp,image/gif'

const ASSUNTO_KEYS = ['sugestoes', 'duvidas', 'suporte-tecnico', 'pagamento', 'estornos'] as const
type AssuntoKey = typeof ASSUNTO_KEYS[number]

export function ContactForm() {
  const t = useTranslations('contato')
  // Written out rather than `t(`subjects.${key}`)` so catalogue-coverage.spec
  // can see every key; `satisfies` keeps it in lockstep with ASSUNTO_KEYS.
  const subjectLabel = {
    'sugestoes': t('subjects.sugestoes'),
    'duvidas': t('subjects.duvidas'),
    'suporte-tecnico': t('subjects.suporte-tecnico'),
    'pagamento': t('subjects.pagamento'),
    'estornos': t('subjects.estornos'),
  } satisfies Record<AssuntoKey, string>
  const [nome, setNome] = useState('')
  const [celular, setCelular] = useState('')
  const [email, setEmail] = useState('')
  const [assunto, setAssunto] = useState<AssuntoKey | undefined>(undefined)
  const [mensagem, setMensagem] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(false)
  const [notice, setNotice] = useState<{ variant: 'success' | 'error', message: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const idempotencyKeyRef = useRef<string | null>(null)

  function onFilesPicked(list: FileList | null) {
    if (!list?.length)
      return
    const next = [...files]
    for (let i = 0; i < list.length; i++) {
      const f = list.item(i)
      if (f && next.length < 5)
        next.push(f)
    }
    setFiles(next)
    if (fileInputRef.current)
      fileInputRef.current.value = ''
  }

  function removeFile(index: number) {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setNotice(null)

    if (!assunto) {
      setNotice({ variant: 'error', message: t('errors.selectSubject') })
      return
    }

    setLoading(true)
    try {
      if (!idempotencyKeyRef.current)
        idempotencyKeyRef.current = crypto.randomUUID()

      const fd = new FormData()
      fd.set('nome', nome.trim())
      fd.set('celular', celular.trim())
      fd.set('email', email.trim())
      fd.set('assunto', assunto)
      fd.set('mensagem', mensagem.trim())
      for (const f of files) {
        fd.append('attachments', f)
      }

      const res = await fetch('/api/contato', {
        method: 'POST',
        headers: {
          'X-Idempotency-Key': idempotencyKeyRef.current,
        },
        body: fd,
      })
      const data = (await res.json().catch(() => ({}))) as { error?: string }

      if (!res.ok) {
        if (res.status === 409)
          idempotencyKeyRef.current = null
        setNotice({
          variant: 'error',
          message: data.error ?? t('errors.sendFailed'),
        })
        return
      }

      idempotencyKeyRef.current = null
      setNotice({
        variant: 'success',
        message: t('successMessage'),
      })
      setNome('')
      setCelular('')
      setEmail('')
      setAssunto(undefined)
      setMensagem('')
      setFiles([])
    }
    catch {
      setNotice({
        variant: 'error',
        message: t('errors.network'),
      })
    }
    finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5">
      <div className="grid gap-2">
        <Label htmlFor="contato-nome">{t('fields.name')}</Label>
        <Input
          id="contato-nome"
          name="nome"
          autoComplete="name"
          required
          minLength={2}
          maxLength={200}
          value={nome}
          onChange={e => setNome(e.target.value)}
        />
      </div>

      <div className="grid gap-2 sm:grid-cols-2 sm:gap-4">
        <div className="grid gap-2">
          <Label htmlFor="contato-celular">{t('fields.phone')}</Label>
          <Input
            id="contato-celular"
            name="celular"
            type="tel"
            autoComplete="tel"
            required
            minLength={8}
            maxLength={32}
            placeholder="(00) 00000-0000"
            value={celular}
            onChange={e => setCelular(e.target.value)}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="contato-email">{t('fields.email')}</Label>
          <Input
            id="contato-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="contato-assunto">{t('fields.subject')}</Label>
        <Select value={assunto} onValueChange={v => setAssunto(v as AssuntoKey)}>
          <SelectTrigger id="contato-assunto" className="w-full">
            <SelectValue placeholder={t('fields.subjectPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {ASSUNTO_KEYS.map(key => (
              <SelectItem key={key} value={key}>
                {subjectLabel[key]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="contato-mensagem">{t('fields.message')}</Label>
        <Textarea
          id="contato-mensagem"
          name="mensagem"
          required
          minLength={10}
          maxLength={10_000}
          rows={6}
          placeholder={t('fields.messagePlaceholder')}
          value={mensagem}
          onChange={e => setMensagem(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          {mensagem.length.toLocaleString()}
          {' / 10.000'}
        </p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="contato-anexos">{t('fields.attachments')}</Label>
        <p className="text-xs text-muted-foreground">{t('fields.attachmentsHint')}</p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            id="contato-anexos"
            type="file"
            accept={ACCEPT_IMAGES}
            multiple
            className="sr-only"
            onChange={e => onFilesPicked(e.target.files)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={files.length >= 5}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="size-4" aria-hidden />
            {t('attachImages')}
          </Button>
        </div>
        {files.length > 0 && (
          <ul className="flex flex-col gap-1.5 rounded-md border border-border/60 bg-muted/20 p-3 text-sm">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-muted-foreground">{f.name}</span>
                <button
                  type="button"
                  className={cn(
                    'inline-flex size-8 shrink-0 items-center justify-center rounded-md',
                    'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                  aria-label={t('removeFile', { name: f.name })}
                  onClick={() => removeFile(i)}
                >
                  <X className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={notice !== null} onOpenChange={open => !open && setNotice(null)}>
        <DialogContent aria-describedby={notice ? 'contato-notice-desc' : undefined}>
          <DialogHeader className="sm:text-left">
            <div className="flex items-start gap-3">
              {notice?.variant === 'success'
                ? (
                    <CheckCircle2
                      className="mt-0.5 size-6 shrink-0 text-success"
                      aria-hidden
                    />
                  )
                : notice
                  ? <AlertCircle className="mt-0.5 size-6 shrink-0 text-destructive" aria-hidden />
                  : null}
              <div className="min-w-0 flex-1 space-y-2">
                <DialogTitle id="contato-notice-title">
                  {notice?.variant === 'success' ? t('noticeSuccessTitle') : t('noticeErrorTitle')}
                </DialogTitle>
                {notice
                  ? (
                      <DialogDescription id="contato-notice-desc" className="text-left">
                        {notice.message}
                      </DialogDescription>
                    )
                  : null}
              </div>
            </div>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={() => setNotice(null)}>
              {t('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Button type="submit" disabled={loading} className="w-full sm:w-auto">
        {loading ? t('sending') : t('send')}
      </Button>
    </form>
  )
}
